/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

// Component imports
import { Avatar, Button, Dropdown, Grid, Layout, Popover, Space, Typography, Badge, Divider } from 'antd';
import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons';
import BotTestButtons from '../UI/BotTestButtons';
import NotificationsListContainer from '../UI/NotificationsListContainer';
import OrgSelector from './OrgSelector';
import BoxIcon from '../Icons/BoxIcon';

// Misc imports
import { NAV_LOGOUT, NAV_ROOT } from '../../constants/navigation';
import { IS_LIVECHAT_CONSOLE_ONLY, IS_NOTIFICATIONS_ENABLED } from '../../constants/env';
import { useFetchBotQuery } from '../../store/bots/botsApi';
import { useFetchUnreadNotificationCountQuery } from '../../store/notifications/notificationsApi';
import { getInitials } from '../../utils/string/getInitials';
import { toggleSidebarOpen } from '../../store/global/globalActions';

import AppLogo from '../../assets/logo.svg';
import AppLogoFull from '../../assets/logo-full.svg';
import BotAvatar from '../UI/BotAvatar';
import BotSelector from './BotSelector';

// Constant declarations
const { Header } = Layout;
const { useBreakpoint } = Grid;

const baseClassName = 'global-header';

const GlobalHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isLargeScreenSize = screens.lg;
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();

  const { currentUser } = useSelector((state) => state.auth);
  const { data: bot, isFetching: isFetchingBot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const { data: unreadNotificationCountResponse } = useFetchUnreadNotificationCountQuery({ orgId, userId: currentUser.id }, { skip: IS_LIVECHAT_CONSOLE_ONLY || !IS_NOTIFICATIONS_ENABLED });
  const { isFullScreenMode, isSidebarOpen, pageTitle } = useSelector((state) => state.global);

  const [isNotificationsPopoverOpen, setIsNotificationsPopoverOpen] = useState(false);
  const unreadNotificationCount = unreadNotificationCountResponse?.unreadNotificationCount || 0;

  const handleUserProfileClick = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('isProfileFormModalOpen', true);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('My Profile'),
      onClick: handleUserProfileClick
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('Log Out'),
      onClick: () => navigate(NAV_LOGOUT)
    }
  ];

  const LogoImage = (
    <div className={`${baseClassName}__logo__image`}>
      <img
        src={isLargeScreenSize && isSidebarOpen ? AppLogoFull : AppLogo}
        style={{ marginLeft: isLargeScreenSize ? -8 : 0 }}
        alt="logo"
      />
    </div>
  );

  return (
    <Header
      className={classNames(baseClassName, {
        'd-none': isFullScreenMode,
        [`${baseClassName}--collapsed`]: !isSidebarOpen
      })}
    >
      {
        IS_LIVECHAT_CONSOLE_ONLY ? (
          <div className={`${baseClassName}__logo`}>
            {LogoImage}
          </div>
        ) : (
          <Link to={NAV_ROOT} className={`${baseClassName}__logo`}>
            {LogoImage}
          </Link>
        )
      }

      <div className="flex-grow-1 d-flex align-items-center">
        {
          !isMobileOrTabletSize ? (
            // Desktop view
            <Fragment>
              <Space
                className="flex-grow-1"
                split={<Divider type="vertical" />}
              >
                {
                  tenantId ? (
                    <BotSelector popoverProps={{ placement: 'bottomLeft' }}>
                      <Button size="large" type="text" className="bg-layout">
                        <Space className="v-align-middle">
                          <BotAvatar bot={bot} avatarSize={22} />
                        </Space>
                      </Button>
                    </BotSelector>
                  ) : null
                }

                <Typography.Text className={`${baseClassName}__title`}>
                  {pageTitle}
                </Typography.Text>
              </Space>

              {
                IS_NOTIFICATIONS_ENABLED && !IS_LIVECHAT_CONSOLE_ONLY ? (
                  <Popover
                    trigger="click"
                    open={isNotificationsPopoverOpen}
                    onOpenChange={setIsNotificationsPopoverOpen}
                    content={
                      <NotificationsListContainer
                        unreadNotificationCount={unreadNotificationCount}
                        onNotificationClick={() => setIsNotificationsPopoverOpen(false)}
                      />
                    }
                    showArrow={false}
                    overlayClassName="p-5 border-radius-base"
                  >
                    <Button type="text" className="h-100 p-2 mr-2">
                      <Badge count={unreadNotificationCount}>
                        <Avatar
                          size={26}
                          icon={<BoxIcon name="bell" className="fs-xxl text-color-default" />}
                          style={{ backgroundColor: 'transparent' }} // Leave this as style, when moved to className this is being ignored for the Bell notification icon
                        />
                      </Badge>
                    </Button>
                  </Popover>
                ) : null
              }

              <OrgSelector />

              <Dropdown menu={{ items: userMenuItems }}>
                <Avatar
                  size={41}
                  shape="square"
                  className="bg-layout text-color-default ml-2 mr-5 cursor-pointer"
                  src={currentUser?.image}
                >
                  {getInitials(currentUser?.fullName ?? '') || <UserOutlined className="v-align-middle fs-lg" />}
                </Avatar>
              </Dropdown>
            </Fragment>
          ) : (
            // Mobile view
            <Fragment>
              <div className="flex-grow-1"/>

              <OrgSelector className="p-2" />

              {
                tenantId ? (
                  <Fragment>
                    <BotTestButtons disabled={isFetchingBot && !bot} />

                    <Button
                      type="text"
                      onClick={() => dispatch(toggleSidebarOpen(!isSidebarOpen))}
                      className="mx-2"
                    >
                      <MenuOutlined className="fs-lg" />
                    </Button>
                  </Fragment>
                ) : null
              }
            </Fragment>
          )
        }
      </div>

      <div className={`${baseClassName}__border`}/>
    </Header>
  );
};

export default GlobalHeader;
